/**
 * OptionsSection — small "Options" collapsible at the bottom of the ATS
 * sidebar. Currently only exposes the "screen-reader-only labels" toggle
 * which controls whether hidden accessibility labels are rendered on
 * ATS-sensitive fields.
 */

import { ChevronDown, ChevronRight, Settings } from 'lucide-react';
import type { getTType } from '../../contexts/I18nContext';

export interface OptionsSectionProps {
  t: getTType;
  isMobile: boolean;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  srOnlyLabelsEnabled: boolean;
  setSrOnlyLabelsEnabled: (updater: (prev: boolean) => boolean) => void;
}

export function OptionsSection({
  t,
  isMobile,
  isExpanded,
  onToggleExpanded,
  srOnlyLabelsEnabled,
  setSrOnlyLabelsEnabled,
}: OptionsSectionProps) {
  return (
    <div className="space-y-4 border-t border-white/10 pt-4">
      <h2
        className={`flex items-center justify-between gap-2 text-base font-semibold text-white cursor-pointer hover:text-blue-300 transition-colors border-t border-white/10 pt-4 ${
          isMobile ? 'hidden' : ''
        }`}
        onClick={onToggleExpanded}
      >
        <span className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-blue-600/20 flex items-center justify-center text-blue-400">
            <Settings size={18} />
          </div>
          Options
        </span>
        {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
      </h2>
      {isExpanded && (
        <div className="flex items-center justify-between gap-2 p-2 rounded-lg bg-white/5 border border-white/10">
          <label
            className="text-sm text-white/60 font-medium cursor-pointer flex-1"
            htmlFor="sr-only-toggle"
          >
            {t('options.srOnlyLabels')}
          </label>
          <button
            id="sr-only-toggle"
            type="button"
            role="switch"
            aria-checked={srOnlyLabelsEnabled}
            onClick={() => setSrOnlyLabelsEnabled((prev) => !prev)}
            className={`w-10 h-5 rounded-full transition-all ${
              srOnlyLabelsEnabled ? 'bg-blue-600' : 'bg-white/10'
            }`}
          >
            <div
              className={`w-4 h-4 bg-white rounded-full transition-all ${
                srOnlyLabelsEnabled ? 'translate-x-5' : 'translate-x-0.5'
              }`}
            />
          </button>
        </div>
      )}
    </div>
  );
}
